import * as React from 'react';
import styled from '@emotion/styled';
import { useStaticQuery, graphql } from 'gatsby';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { GatsbySite } from '../../graphql';
import SEO from '../../components/seo';
import { SpacingWrapper } from '../../components/utility/spacing-wrapper';
import { CenteredContent } from '../../components/utility/section';
import TitlePage from '../../components/project/TitlePage';
import ResponsiveDiv from '../../components/utility/ResponsiveDiv';
import ResponsiveParagraph from '../../components/utility/ResponsiveParagraph';
import Section from '../../components/project/section';
import { HideOnMobile } from '../../components/utility/hide-on';
import Tech from '../../components/project/tech';
import Client from '../../components/project/client';
import Carousel from '../../components/project/carousel';
import Video from '../../components/video';
import Quote from '../../components/project/quote';
import { SectionTag } from '../../components/project/section-tag';
import NextMissions from '../../components/project/next-missions';
// import photos
import hero from '../../images/projects/earth-drilling/hero.png';
import summaryGraph from '../../images/projects/earth-drilling/summary-graph.png';
import robbie from '../../images/projects/employee-avatars/robbie.png';
import reactLogo from '../../images/tech-logos/react-logo.png';
import jobSummary from '../../images/projects/earth-drilling/job-summary.png';
import pointingAtLaptop from '../../images/projects/earth-drilling/pointing-at-laptop.png';
import manUsingApp from '../../images/projects/earth-drilling/man-using-app.png';
import background from '../../images/projects/enjoyn/background.png';
import nodeLogo from '../../images/tech-logos/node-logo.png';
import earthDrillingLogo from '../../images/projects/earth-drilling/earth-drilling-logo.png';
import carousel2 from '../../images/projects/earth-drilling/carousel-2.png';
import carousel3 from '../../images/projects/earth-drilling/carousel-3.png';
import menuVideo from '../../videos/projects/earth-drilling/menu-video.mp4';

const EarthDrillingPage = () => {
  const { site } = useStaticQuery<GatsbySite>(
    graphql`
      query {
        site {
          siteMetadata {
            projects {
              name
              description
              imageName
              link
            }
          }
        }
      }
    `,
  );

  const { siteMetadata } = site;

  const projects = siteMetadata?.projects || [];
  return (
    <>
      <SEO title="Earth Drilling" />
      <div id="#top" />
      <TitlePage
        title="Earth Drilling: Quote to cash"
        content="An all-in-one custom solution that facilitates scalability and simplifies everything from quotations, operations and invoicing to real-time data"
        backgroundImage={background}
      />

      <SpacingWrapper>
        <Client
          content={`Earth Drilling began life in Saskatchewan in 1976, primarily as a water well drilling company. Over the course of the next four and a half decades, the company changed ownership, expanded operations and its services, now including a variety of rigs for geotechnical and environmental drilling. The company focuses on obtaining soil and core samples, to help remediation, construction and development processes.`}
          images={[{ image: earthDrillingLogo, url: 'https://www.earthdrilling.ca/' }]}
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img src={hero} alt="Earth Drilling app and its features" style={{ width: '100%' }} />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`The Quote To Cash project for Earth Drilling demonstrates Launchcode’s abilities to deliver end-to-end solutions for custom workflows and a variety of industries. 
          Earth Drilling undertook multiple drilling projects across various geographies. With several clients on their ledgers, multiple services and different contracts, the company officials had to juggle Excel sheets and manual quotations.`}
          p2={`Launchcode developers not only created an efficient, easy-to-use quotation platform, that featured customizable rates, specifications and templates, but also apps for their in-field personnel to track real-time costs, equipment requirements, timesheets, jobs executed, safety protocols, etc. 
          The all-powerful tool could also generate invoices as and when the jobs were completed. 
          End to end. Quote to cash. A to Z. Promised and delivered. `}
          heading="Creating End-To-End Solutions "
          subHeading="summary"
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={summaryGraph}
              alt="A laptop displaying the job summary page"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`Land drilling as an operation can be quite diverse, right from industries to solutions to goals. Oil and natural gas industries will have different needs than water well drilling, which will be different from geotechnical, environmental and geothermal drilling. 
          According to many reports, North America dominates the land rig market, even as Asia Pacific has emerged as the fastest growing market¹. 
          Earth Drilling as a contractor serves all these niches and consequently has a diverse set of clientele. 
          Add to that, a cumbersome and costly manual quotation and invoicing process using paperwork that dents the environment too. Passing on all those details from sales to operations to finance, further complicates the process besides creating "source of truth" issues. `}
          p2={`Once quotations turn into jobs, keeping track of operational aspects (personnel, equipment, safety protocols) and financial factors (costs, margins, profitability) becomes important but most of the time, challenging. 
          Most off-the-shelf solutions do not fit custom workflows with each industry and company understandably different from the next. 
          While Excel and other spreadsheets are leaps and bounds ahead of their predecessors, scalability can be an issue as companies and operations grow. `}
          heading="From Manual Workflows To Process Automation "
          subHeading="pain"
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <Quote
          quote="There was a disconnect from what was happening in field operations to what was happening within the organization. The challenge was to really create a one-touch system that was a source of truth for them."
          citation="Robbie Butchart"
          citation2="CRO @ Launchcode"
          picture={robbie}
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <SectionTag>vision</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>A long wishlist, delivered</strong>
            </h2>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              A custom solution needs to fit the client workflow, be easy to use, and efficient, to
              say the least.
              <br />
              <br />
              The software needed to be able to deal with a diverse set of clients, their needs,
              Earth Drilling's services, rates and quotations, pass on the approved contract details
              to operations, job execution and invoicing.
              <br />
              <br />
              How about adding on-site requirements and documentation to the wishlist too? Sure.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <SpacingWrapper>
        <Tech
          content={`Given at least two aspects of the solution - sales and administration, and operations - the solution had to be intuitive, easy to use and web browser-based.
              React became an obvious choice to ensure a top-notch intuitive solution and its need for minimal maintenance and support across platforms.`}
          images={[reactLogo, nodeLogo]}
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <Carousel
          images={[
            { image: jobSummary, alt: 'A view of the job summary page' },
            { image: carousel2, alt: 'Adding an expense' },
            { image: carousel3, alt: 'The expenses page' },
          ]}
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`Launchcode has always taken pride in trying to understand the fundamentals of a client's business before moving forward with building a solution. The approach is at the heart of what Launchcode does. 
          With Earth Drilling, multiple sprints focused on understanding the needs of the personnel that were to use the solution once implemented. 
          There was also the case of exporting data out of the old system, and into the new. 
          The need for custom rates and templates (service, equipment and client-focused) became evident. The team also worked on the ability to add custom fields that allowed adding of additional items to the estimates. The quotation process also underlined the need for a visually-appealing document for submission to the client. `}
          p2={`Sprints then veered towards creating a bridge between sales and operations, and handing over accurate job-related information and requirements that would help the latter. That included automating a list of rigs and tools needed to complete a particular job according to the quotation and setting up schedules for personnel. 
          To ensure continuity and accuracy in operations, the in-field crew had access to all the information, besides being able to access safety protocols and timesheets. 
          The ability to create on-site work logs was yet another source of reliable operational data. 
           `}
          heading="In The Quest Of Everything"
          subHeading="journey"
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <HideOnMobile breakpoint={1024}>
              <Video title="A view of the menu" src={menuVideo} />
            </HideOnMobile>
          </ResponsiveParagraph>

          <ResponsiveParagraph>
            <SectionTag>ux/ui</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>What you see is what you get</strong>
            </h2>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              The solution was expected to be used by two sets of user profiles at the minimum. The
              sales and administration personnel at the offices, and the on-site crew.
              <br />
              <br />
              The interface was designed to ensure that sales and administration personnel were
              allowed to fill up as much information about the job as possible for an accurate
              quotation and handover process.
              <br />
              <br />
              Once all the data reached the in-field crew, it was imperative to consider the rigors
              of working at a drilling rig site. Using a mouse can be a pain even in an office
              environment, now consider a rig! The on-site crew-specific forms were especially made
              keyboard-accessible.
              <br />
              <br />
              Some of the interfaces were also designed to be mobile-responsive depending on the
              objectives and user profiles. Aren't filling out timesheets so much simpler on your
              phone?
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`The solution for Earth Drilling emerged as a one-of-a-kind end-to-end application for a very niche industry that not only hit all the KPIs set by the client but also beat expectations. 
          What emerged out of an extensive 6-month agile development process was an all-in-one tool that helped everyone at the client's end - from sales to operations to finance and audit. 
          Through a meticulous and qualitative process, the developers were able to create a real-time cost tracking system with the on-site crew being able to fill in timesheets on the go, besides logging daily job reports and equipment usage.`}
          p2={`Process automation created safety documentation, equipment and personnel lists based on approved quotations, with all internal stakeholders getting real-time access to all the information. 
          With the power of data at their fingertips, the clients were able to make informed decisions regarding future strategy and projects, while also creating process and operational benchmarks. 
          The implementation of the application was able to replace cumbersome manual paperwork, that also made archival and referral processes simpler and easier. 
          And just imagine the positive ecological impact of ending paperwork! `}
          subHeading="results"
          heading="Quote To Cash Made Easy "
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={pointingAtLaptop}
              alt="A hand pointing at a laptop displaying the app"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>

      <SpacingWrapper>
        <Quote
          quote="The Launchcode team was able to adapt to our requests without missing a beat. The creative process was refreshing, and the results have been amazing. Launchcode has enabled our company to become more efficient through the development of this platform which has helped reduce our operational cost and improve our client experience at the same time."
          citation="Chris Gourlie"
          citation2="Vice-President @ Earth Drilling "
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`The quote to cash solution was the result of a creative developmental process that has been dubbed as “refreshing” by the clients. 
          The application resulted in massive cost and time savings at different levels. At the quotation end of the process, the solution resulted in average cost savings of more than 90% annually with a quick return on investment. 
          Before quote to cash was implemented at Earth Drilling, depending on the length of the projects and the ability of personnel to report from their respective sites, the HQ personnel had to deal with an average 10-day delay in tracking of operational costs and exigencies. `}
          p2={`The application, with its real-time tracking capabilities, eliminated delays and gave decision-makers the ability to adjust projects on the fly. 
          The process automation aspect of the solution ensured that all the jobs passed any health and safety audits, while also ensuring reliability of data. 
          The client's clients especially appreciated the additional structured data and information that the application made possible. 
          Collectively, all these aspects of the solution came together to ensure scalability of operations and allowed Earth Drilling to focus on things that matter.`}
          subHeading="response"
          heading="A Refreshing Process For An Innovative Solution"
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={manUsingApp}
              alt="A man viewing the app on a laptop"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>

      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <SectionTag>next steps</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>Innovation never rests</strong>
            </h2>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              As of June 2022, Launchcode developers continue to support and maintain the Quote To
              Cash solution for Earth Drilling in a managed service model. Launchcode continues to
              host the backend and database, and works to remove any bugs.
              <br />
              <br />
              Launchcode was also engaged further to add functionalities based on the changing
              operational landscape of the client.
              <br />
              <br />
              The sound technological foundation and the amazing client response make it easy to
              consider other additions, including a field application for on-site crew, integrating
              payroll and accounting functions.
              <br />
              <br />
              Based on the feedback we have received, a solution like this is something that
              industry would definitely appreciate.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <CitationsTitleWrapper>
              <h2
                style={{
                  fontWeight: 500,
                  fontSize: 48,
                  maxWidth: 730,
                  textAlign: 'left',
                }}
              >
                <strong>Citations</strong>
              </h2>
            </CitationsTitleWrapper>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p>
              <sup>1</sup>
              <a
                href="https://www.mordorintelligence.com/industry-reports/land-drilling-rig-market"
                target="_blank"
                rel="nofollow noreferrer"
              >
                “Land Drilling Rig Market - Growth, Trends, Covid-19 Impact, And Forecasts (2022 -
                2027).”
              </a>{' '}
              Mordor Intelligence. Accessed 9 June 2022.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <NextMissions currentProjectName="Earth Drilling" projects={projects} />

      <CenteredContent style={{ marginBottom: 40 }}>
        <a href="#top">Back to top ⇮</a>
      </CenteredContent>
    </>
  );
};

const SimpleUXImageWrapper = styled.div`
  > div {
    overflow: unset;
    transform: scale(1.075);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    img {
      top: 0%;
    }
  }
`;

const CitationsTitleWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.xs}) {
    margin: 0px 0px 0px 0px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin: 0px 0px 0px 0px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-left: 40px;
  }

  @media screen and (min-width: 1250px) {
    margin-left: 40px;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.wideScreen}) {
    margin-left: 40px;
  }
`;

export default EarthDrillingPage;
